import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MenuList, MenuListProps, useMergeRefs } from '@chakra-ui/react';
import { callAllHandlers } from '@allergan-data-labs/alle-elements-core';
import { getActionContentStyles } from './action-menu-content.theme';
import { ActionMenuContext } from './action-menu-context';

const ACCORDION_CLASSNAME = 'action-menu-accordion__button';
const ACCORDION_PANEL_CLASSNAME = '.chakra-collapse';

export const canFocusElement = (element: HTMLButtonElement): boolean => {
  // Check if element is nested under a hidden accordion
  if (
    element.closest(ACCORDION_PANEL_CLASSNAME) !== null &&
    (element.closest(ACCORDION_PANEL_CLASSNAME) as HTMLElement)?.style
      ?.display === 'none'
  ) {
    return false;
  }

  // Check if element is disabled
  if (element.disabled) {
    return false;
  }

  return true;
};

export const getActiveIndex = (
  focusedItemIndex: number,
  registeredItems: NodeListOf<HTMLButtonElement>
): number => {
  if (focusedItemIndex > -1) {
    return focusedItemIndex;
  }

  const activeElement = document.activeElement;

  if (activeElement === null) {
    return -1;
  }

  if (registeredItems && registeredItems?.length > 0) {
    let activeIndex = -1;
    registeredItems.forEach((item, index) => {
      if (item.id === activeElement.id) {
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  return -1;
};

export type ActionMenuContentProps = {
  children?: React.ReactNode;
} & Pick<MenuListProps, 'onKeyDown' | 'onMouseLeave' | 'sx'>;

/**
 * The `ActionMenuContent` component is a direct child of the `ActionMenu`
 * component that all of the other `ActionMenu*` components must be nested within
 */
export const ActionMenuContent = forwardRef(
  ({ children, ...props }: ActionMenuContentProps, ref) => {
    const { colorMode, isOpen } = useContext(ActionMenuContext);
    const [registeredItems, setRegisteredItems] =
      useState<NodeListOf<HTMLButtonElement>>();
    const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
    const [hasAccordion, setHasAccordion] = useState<boolean>(false);
    const [checkedForAccordion, setCheckedForAccordion] =
      useState<boolean>(false);
    const menuList = useRef<HTMLDivElement | null>(null);

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!hasAccordion && checkedForAccordion) {
        return;
      }

      const keyCode = event.code;
      const allowedKeys = ['ArrowDown', 'ArrowUp'];

      if (
        allowedKeys.includes(keyCode) &&
        registeredItems &&
        registeredItems?.length > 0
      ) {
        event.stopPropagation();
        event.preventDefault();

        const focusIndices: number[] = [];
        const currentFocusedIndex = getActiveIndex(
          focusedItemIndex,
          registeredItems
        );

        registeredItems.forEach((item, idx) => {
          if (canFocusElement(item)) {
            focusIndices.push(idx);
          }
        });

        if (focusIndices.length === 0) {
          // Nothing to focus on
          return;
        }

        let nextIndex: number | undefined;
        if (keyCode === 'ArrowDown') {
          nextIndex = focusIndices.find((idx) => idx > currentFocusedIndex);
        }

        if (keyCode === 'ArrowUp') {
          nextIndex = focusIndices
            .reverse()
            .find((idx) => idx < currentFocusedIndex);
        }

        if (nextIndex === undefined) {
          nextIndex = focusIndices[0];
        }

        if (nextIndex !== undefined && nextIndex > -1) {
          registeredItems[nextIndex].focus();

          setFocusedItemIndex(nextIndex);
        }
      }
    };

    useEffect(() => {
      if (!isOpen) {
        setFocusedItemIndex(-1);
      }
    }, [setFocusedItemIndex, isOpen]);

    useEffect(() => {
      if (!hasAccordion && checkedForAccordion) {
        return;
      }

      if (menuList.current !== null) {
        const allDescendants = menuList.current.querySelectorAll('button');

        if (!checkedForAccordion) {
          let foundAccordion = false;

          allDescendants.forEach((button) => {
            if (button.classList.contains(ACCORDION_CLASSNAME)) {
              foundAccordion = true;
            }
          });

          if (!foundAccordion) {
            setCheckedForAccordion(true);
            return;
          }
        }

        setHasAccordion(true);
        setRegisteredItems(allDescendants);

        if (focusedItemIndex > -1) {
          allDescendants[focusedItemIndex].focus();
        }
      }
    }, [
      focusedItemIndex,
      setRegisteredItems,
      hasAccordion,
      setHasAccordion,
      checkedForAccordion,
      setCheckedForAccordion,
    ]);

    let hasFooter = false;

    if (Array.isArray(children)) {
      children.forEach((child) => {
        if (child.type.displayName === 'ActionMenuFooter') {
          hasFooter = true;
        }
      });
    }

    return (
      <MenuList
        data-testid="actionMenuContent"
        onKeyDown={callAllHandlers(props.onKeyDown, onKeyDownHandler)}
        ref={useMergeRefs(ref, menuList)}
        {...props}
        sx={{
          ...getActionContentStyles(colorMode, hasFooter),
          ...props?.sx,
        }}
      >
        {children}
      </MenuList>
    );
  }
);
